import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { api, Profile, UserRole } from '../lib/api';
import { auth0Service } from '../lib/auth0Service';

interface User {
  id: string;
  email: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, role: UserRole, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchRole: (newRole: UserRole) => Promise<void>;
  refreshProfile: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();

  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      // First try to get token from Auth0 SDK (if authenticated via redirect)
      if (isAuthenticated) {
        return await getAccessTokenSilently();
      }
      // Otherwise, get from localStorage (set by direct authentication)
      return localStorage.getItem('auth0_access_token') || undefined;
    } catch (error) {
      console.error('Error getting access token:', error);
      // Fallback to localStorage
      return localStorage.getItem('auth0_access_token') || undefined;
    }
  };

  const fetchProfile = async (): Promise<Profile | null> => {
    try {
      const token = await getAccessToken();
      if (token) {
        api.setToken(token);
      }
      // Use /profiles/me endpoint - the backend will identify the user from the auth token
      const endpoint = '/profiles/me';
      const data = await api.get<Profile>(endpoint);
      return data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
  };

  const refreshProfile = async () => {
    const profileData = await fetchProfile();
    setProfile(profileData);
  };

  useEffect(() => {
    const initAuth = async () => {
      // Check if we have a token from direct authentication (not redirect)
      const storedToken = localStorage.getItem('auth0_access_token');
      const storedUser = localStorage.getItem('auth0_user');

      if (storedToken && storedUser) {
        try {
          // Use stored user info
          const userData = JSON.parse(storedUser);
          const mappedUser: User = {
            id: userData.sub || userData.user_id || '',
            email: userData.email || '',
            ...userData,
          };

          setUser(mappedUser);
          api.setToken(storedToken);
          
          // Set Sentry user context
          Sentry.setUser({
            id: mappedUser.id,
            email: mappedUser.email,
          });

          // Fetch profile
          let profileData = await fetchProfile();
          
          // If no profile exists, check if we have role info
          if (!profileData) {
            const pendingRole = localStorage.getItem('pending_role');
            const pendingFullName = localStorage.getItem('pending_fullName');
            
            if (pendingRole) {
              try {
                // Link Auth0 user to application user system first
                await api.post('/auth/link', {
                  email: mappedUser.email,
                  fullName: pendingFullName || null,
                  role: pendingRole,
                });
                
                // Then create profile
                await api.post('/profiles', {
                  id: mappedUser.id,
                  email: mappedUser.email,
                  role: pendingRole,
                  full_name: pendingFullName || null,
                });
                localStorage.removeItem('pending_role');
                localStorage.removeItem('pending_fullName');
                profileData = await fetchProfile();
              } catch (error) {
                console.error('Error linking user or creating profile:', error);
              }
            }
          }
          
          setProfile(profileData);
          setLoading(false);
          return;
        } catch (error) {
          console.error('Error loading stored auth:', error);
          // Clear invalid stored data
          localStorage.removeItem('auth0_access_token');
          localStorage.removeItem('auth0_user');
        }
      }

      // Fallback to Auth0 SDK (redirect-based auth)
      if (auth0Loading) {
        setLoading(true);
        return;
      }

      if (isAuthenticated && auth0User) {
        try {
          const mappedUser: User = {
            id: auth0User.sub || auth0User.user_id || '',
            email: auth0User.email || '',
            ...auth0User,
          };

          setUser(mappedUser);
          const token = await getAccessToken();
          if (token) {
            api.setToken(token);
          }
          
          // Set Sentry user context
          Sentry.setUser({
            id: mappedUser.id,
            email: mappedUser.email,
          });

          let profileData = await fetchProfile();
          setProfile(profileData);
        } catch (error) {
          console.error('Error initializing auth:', error);
        }
      } else {
        setUser(null);
        setProfile(null);
        
        // Clear Sentry user context
        Sentry.setUser(null);
        api.setToken(null);
      }

      setLoading(false);
    };

    initAuth();
  }, [isAuthenticated, auth0User, auth0Loading, getAccessTokenSilently]);

  const signUp = async (email: string, password: string, role: UserRole, fullName: string) => {
    // Store role for profile creation
    localStorage.setItem('pending_role', role);
    if (fullName) {
      localStorage.setItem('pending_fullName', fullName);
    }

    // Sign up with Auth0 directly
    const signUpResponse = await auth0Service.signUp(email, password, fullName);
    
    // After signup, automatically sign in
    const tokenResponse = await auth0Service.signIn(email, password);
    
    // Get user info
    const userInfo = await auth0Service.getUserInfo(tokenResponse.access_token);
    
    // Store tokens and user info
    localStorage.setItem('auth0_access_token', tokenResponse.access_token);
    if (tokenResponse.id_token) {
      localStorage.setItem('auth0_id_token', tokenResponse.id_token);
    }
    localStorage.setItem('auth0_user', JSON.stringify(userInfo));
    
    // Map Auth0 user to our User interface
    const mappedUser: User = {
      id: userInfo.sub || '',
      email: userInfo.email || '',
      ...userInfo,
    };

    setUser(mappedUser);
    api.setToken(tokenResponse.access_token);
    
    // Set Sentry user context
    Sentry.setUser({
      id: mappedUser.id,
      email: mappedUser.email,
    });

    // Link Auth0 user to application user system
    try {
      await api.post('/auth/link', {
        email: mappedUser.email,
        fullName: fullName || null,
        role,
      });
    } catch (error) {
      console.error('Error linking Auth0 user:', error);
      throw error; // Re-throw to prevent profile creation if linking fails
    }

    // Create profile with the role
    try {
      await api.post('/profiles', {
        id: mappedUser.id,
        email: mappedUser.email,
        role,
        full_name: fullName || null,
      });
      localStorage.removeItem('pending_role');
      localStorage.removeItem('pending_fullName');
      
      // Fetch the newly created profile
      const profileData = await fetchProfile();
      setProfile(profileData);
    } catch (error) {
      console.error('Error creating profile:', error);
      // Still set user even if profile creation fails
      setProfile(null);
    }
  };

  const signIn = async (email: string, password: string) => {
    // Sign in with Auth0 directly
    const tokenResponse = await auth0Service.signIn(email, password);
    
    // Get user info
    const userInfo = await auth0Service.getUserInfo(tokenResponse.access_token);
    
    // Store tokens and user info
    localStorage.setItem('auth0_access_token', tokenResponse.access_token);
    if (tokenResponse.id_token) {
      localStorage.setItem('auth0_id_token', tokenResponse.id_token);
    }
    localStorage.setItem('auth0_user', JSON.stringify(userInfo));
    
    // Map Auth0 user to our User interface
    const mappedUser: User = {
      id: userInfo.sub || '',
      email: userInfo.email || '',
      ...userInfo,
    };

    setUser(mappedUser);
    api.setToken(tokenResponse.access_token);
    
    // Set Sentry user context
    Sentry.setUser({
      id: mappedUser.id,
      email: mappedUser.email,
    });

    // Fetch profile
    const profileData = await fetchProfile();
    setProfile(profileData);
  };

  const signOut = async () => {
    // Clear local storage
    localStorage.removeItem('auth0_access_token');
    localStorage.removeItem('auth0_id_token');
    localStorage.removeItem('auth0_user');
    localStorage.removeItem('pending_role');
    localStorage.removeItem('pending_fullName');
    
    // Clear state
    setUser(null);
    setProfile(null);
    api.setToken(null);
    
    // Clear Sentry user context
    Sentry.setUser(null);
    
    // Also logout from Auth0 SDK if authenticated via redirect
    if (isAuthenticated) {
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  };

  const switchRole = async (newRole: UserRole) => {
    if (!user) return;

    const token = await getAccessToken();
    if (token) {
      api.setToken(token);
    }

    await api.patch(`/profiles/me`, { role: newRole });
    await refreshProfile();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading: loading || auth0Loading,
        signUp,
        signIn,
        signOut,
        switchRole,
        refreshProfile,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
